import { useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import FileUpload from "../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../DesignSystem/Upload/UploadedFileList";
import NewInputField from "../../DesignSystem/Inputs/NewInputField";
import NewMobileNumberInput from "../../DesignSystem/Inputs/NewMobileNumberInput";
import {
  saveRecruitmentApplicantDetails,
  saveReferenceConsentFormToRecruitment,
} from "../services/recruitmentPipelineService";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import "../../../css/Recruitment/ReferenceEmailPreviewModal.css";

const ApplicantReferenceDetailsModal = ({
  open,
  onClose,
  adviserId,
  applicant,
  applicantName,
  onConsentUpdated,
  onDetailsUpdated,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [details, setDetails] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    setSelectedFile(null);
    setMessage("");
    setIsError(false);
    const validPhoneValue = (value) => {
      const rawValue = String(value || "").trim().replace(/^00/, "+");
      const parsedNumber = parsePhoneNumberFromString(rawValue, "GB");
      return parsedNumber?.isValid() ? parsedNumber.number : "";
    };
    setDetails({
      forename: applicant?.forename || applicant?.firstName || "",
      surname: applicant?.surname || applicant?.lastName || "",
      nationalInsuranceNumber: applicant?.nationalInsuranceNumber || "",
      mobileTelephone: validPhoneValue(applicant?.mobileTelephone),
      homeTelephone: validPhoneValue(applicant?.homeTelephone),
      officeTelephone: validPhoneValue(applicant?.officeTelephone),
      emailAddress: applicant?.emailAddress || applicant?.email || "",
      personalEmail: applicant?.personalEmail || "",
      workEmail: applicant?.workEmail || "",
    });
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, applicant]);

  if (!open) return null;

  const consentAttachment = applicant?.referenceConsentAttachment;
  const phoneFields = [
    ["Mobile Telephone", "mobileTelephone"],
    ["Home Telephone", "homeTelephone"],
    ["Office Telephone", "officeTelephone"],
  ];
  const emailFields = [
    ["Email Address", "emailAddress"],
    // ["Personal Email", "personalEmail"],
    // ["Work Email", "workEmail"],
  ];

  const saveDetails = async () => {
    setIsUploading(true);
    setMessage("");
    try {
      await saveRecruitmentApplicantDetails(adviserId, details);
      onDetailsUpdated?.(details);
      if (selectedFile) {
        const attachment = await saveReferenceConsentFormToRecruitment(
          adviserId,
          selectedFile,
        );
        onConsentUpdated?.(attachment);
      }
      setSelectedFile(null);
      setIsError(false);
      setMessage("The adviser details have been saved.");
    } catch (error) {
      setIsError(true);
      setMessage(error?.message || "Unable to upload the consent form.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="reference-email-modal-overlay" role="presentation">
      <section
        className="reference-email-modal gap-m bg-white p-m card-corner-radius"
        role="dialog"
        aria-modal="true"
        aria-label="Adviser reference details"
      >
        <div className="row-space-between gap-m">
          <div className="FP_Subtitle_Semibold text-primary-1">
            Adviser Details{applicantName ? ` - ${applicantName}` : ""}
          </div>
          {/* <button
            type="button"
            className="reference-email-modal-close"
            onClick={onClose}
            aria-label="Close adviser details"
          >
          </button> */}
            <CloseIcon onClick={onClose} />
        </div>

        {message && (
          <AlertBanner
            type={isError ? "error" : "success"}
            message={message}
            showClose={false}
          />
        )}

        <div className="grid2 gap-m">
          <NewInputField
            label="First Name"
            value={details.forename || ""}
            onChange={(event) =>
              setDetails((current) => ({
                ...current,
                forename: event.target.value,
              }))
            }
          />
          <NewInputField
            label="Last Name"
            value={details.surname || ""}
            onChange={(event) =>
              setDetails((current) => ({
                ...current,
                surname: event.target.value,
              }))
            }
          />
          <NewInputField
            label="NI Number"
            value={details.nationalInsuranceNumber || ""}
            onChange={(event) =>
              setDetails((current) => ({
                ...current,
                nationalInsuranceNumber: event.target.value.toUpperCase(),
              }))
            }
          />
          {phoneFields.map((field) => (
            <NewMobileNumberInput
              key={field[1]}
              label={field[0]}
              value={details[field[1]] || ""}
              onChange={(value) =>
                setDetails((current) => ({
                  ...current,
                  [field[1]]: value,
                }))
              }
            />
          ))}
          {emailFields.map(([label, field]) => (
            <NewInputField
              key={field}
              label={label}
              value={details[field] || ""}
              type="email"
              onChange={(event) =>
                setDetails((current) => ({
                  ...current,
                  [field]: event.target.value,
                }))
              }
            />
          ))}
        </div>

        <div className="fit-column gap-s">
          <div className="FP_Content_Semibold">
            Reference Authorisation Form
          </div>
          {consentAttachment && (
            <UploadedFileList files={[consentAttachment]} removable={false} />
          )}
          <FileUpload
            label="Upload or replace consent form"
            description="Drag & drop the signed consent form here or"
            browseText="Browse"
            accept=".pdf,application/pdf"
            files={selectedFile ? [selectedFile] : []}
            onFilesChange={(file) => {
              const nextFile = Array.isArray(file) ? file[0] : file;
              setSelectedFile(nextFile || null);
              setMessage("");
            }}
          />
          {selectedFile && (
            <UploadedFileList
              files={[selectedFile]}
              onRemove={() => setSelectedFile(null)}
            />
          )}
        </div>

        <div className="display-flex justify-center gap-m">
          <PrimaryButton
            type="button"
            label={isUploading ? "Saving..." : "Save"}
            disabled={isUploading}
            onClick={saveDetails}
          />
        </div>
      </section>
    </div>
  );
};

export default ApplicantReferenceDetailsModal;
